import React, { useState } from 'react';
import { IssueChip } from './IssueChip';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { ShotIssueTag } from '@/types/shotIssues';
import { getTagLabel } from '@/types/shotIssues';

interface ShotContextChipPickerProps {
  selectedTags: ShotIssueTag[];
  notes?: string;
  onTagsChange: (tags: ShotIssueTag[]) => void;
  onNotesChange: (notes: string) => void;
  className?: string;
}

// Direction/Curve tags
const DIRECTION_TAGS: ShotIssueTag[] = [
  'started_left',
  'started_right',
  'ended_left',
  'ended_right',
  'straightish',
];

// Contact/Flight tags
const CONTACT_TAGS: ShotIssueTag[] = [
  'fat',
  'thin',
  'topped',
  'shank',
  'toe_contact',
  'heel_contact',
  'high_ball',
  'low_ball',
  'losing_distance',
  'inconsistent_contact',
];

export function ShotContextChipPicker({
  selectedTags,
  notes = '',
  onTagsChange,
  onNotesChange,
  className,
}: ShotContextChipPickerProps) {
  const toggleTag = (tag: ShotIssueTag) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  return (
    <div className={className}>
      {/* Direction / Curve Section */}
      <div className="mb-6">
        <Label className="text-sm uppercase tracking-wide text-gray-500 mb-3 block">
          DIRECTION / CURVE
        </Label>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {DIRECTION_TAGS.map((tag) => (
            <IssueChip
              key={tag}
              label={getTagLabel(tag)}
              selected={selectedTags.includes(tag)}
              mode="multi"
              onSelect={() => toggleTag(tag)}
              className="px-4 py-2 text-sm"
            />
          ))}
        </div>
      </div>

      {/* Contact / Flight Quality Section */}
      <div className="mb-6">
        <Label className="text-sm uppercase tracking-wide text-gray-500 mb-3 block">
          CONTACT / FLIGHT QUALITY
        </Label>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {CONTACT_TAGS.map((tag) => (
            <IssueChip
              key={tag}
              label={getTagLabel(tag)}
              selected={selectedTags.includes(tag)}
              mode="multi"
              onSelect={() => toggleTag(tag)}
              className="px-4 py-2 text-sm"
            />
          ))}
        </div>
      </div>

      {/* Optional Notes */}
      <div>
        <Label htmlFor="shot-notes" className="text-sm text-gray-600 mb-2 block">
          Anything else we should know? (optional)
        </Label>
        <Textarea
          id="shot-notes"
          rows={1}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="e.g., My timing felt off..."
          className="border-2 border-gray-200 rounded-xl focus:border-[#196644] focus:ring-2 focus:ring-[#196644]/20 p-4"
        />
      </div>
    </div>
  );
}

